import { useQueryClient } from "@tanstack/react-query";
import Button from "components/buttons/Button/Button";
import { t } from "i18next";
import { useEffect, useState } from "react";

import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";
import { getDataFromResponse, handleApiError } from "~/api/axiosUtils.ts";
import Headline from "~/components/Headline/Headline";
import Hint from "~/components/Hint";
import Modal from "~/components/Modal";
import Paragraph from "~/components/Paragraph";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import { USERS_ACTIVE_STAFFINGS_QUERY_KEY } from "~/modules/project/api/staffing/staffingQueries.ts";
import { closeMonthlyClosing } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingApiDispatchers.ts";
import { monthlyClosingQueries } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingQueries.ts";
import { CLOSE_REFUSED_RULE, type MonthlyClosingCloseRequest } from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingTypes.ts";
import { dayStateQueries, TIME_TRACKINGS_BY_USER_AND_MONTH_QUERY_KEY } from "~/modules/timeTracking/api/timeTracking/timeTrackingQueries.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";

type Props = {
	/** `YYYY-MM`. */
	month: string;
	onClose: () => void;
	request: MonthlyClosingCloseRequest | null;
	userId: string;
};

// The `rule` is stable, the German `message` is not. So the text of a refused part comes from the rule.
const CLOSE_REFUSED_TEXT = "Dieser Teil kann nicht geschlossen werden. Lade die Seite neu.";

function getRefusalText(error: unknown): string | undefined {
	const result = handleApiError(error);
	if (result.type !== ApiResponseErrorType.VALIDATION) return undefined;

	// The field differs by scope (`staffingIds.N` for staffings), so only the rule decides.
	const isRefused = Object.values(result.messages).some((details) =>
		details.some((detail) => detail.rule === CLOSE_REFUSED_RULE));
	return isRefused ? CLOSE_REFUSED_TEXT : undefined;
}

export function CloseMonthlyClosingModal(props: Props) {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");

	const queryClient = useQueryClient();

	useEffect(() => {
		if (props.request) setError("");
	}, [props.request]);

	const handleConfirmationClick = async () => {
		if (!props.request) return;
		setBusy(true);
		try {
			const read = await closeMonthlyClosing(props.userId, { ...props.request.scope, month: `${props.month}-01` })
				.then(getDataFromResponse);

			queryClient.setQueryData(monthlyClosingQueries.month(props.userId, props.month).queryKey, read);
			queryClient.invalidateQueries({ queryKey: monthlyClosingQueries.openMonths(props.userId).queryKey });
			// The closing decides the `update` and `delete` blocks of the trackings in the month.
			queryClient.invalidateQueries({ queryKey: TIME_TRACKINGS_BY_USER_AND_MONTH_QUERY_KEY(props.userId, props.month) });
			// The dashboard calendar locks its days from the day states, which follow the closing.
			queryClient.invalidateQueries({ queryKey: dayStateQueries.byMonth(props.userId, props.month).queryKey });
			// An ended staffing leaves the list of the dashboard cards after the closing.
			queryClient.invalidateQueries({ queryKey: USERS_ACTIVE_STAFFINGS_QUERY_KEY(props.userId) });
			props.onClose();
		} catch (error) {
			setError(getRefusalText(error) ?? "Ein unbekannter Fehler ist aufgetreten.");
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal open={!!props.request}
			   setOpen={props.onClose}>
			<div className="relative flex flex-col gap-3">
				{busy && <SidebarBusyOverlay/> }
				<Headline type="h4">Monat schließen</Headline>
				<div>
					{!error ? (
						<Hint theme="info">
							<Paragraph>Du schließt {props.request?.label}. Bitte überprüfe alle Erfassungen auf Richtigkeit,
								bevor Du schließt.</Paragraph>
						</Hint>
					) : (
						<Hint theme="error">{error}</Hint>
					)}
				</div>
				<div className="mt-4">
					<Button disabled={busy || !!error}
							onClick={handleConfirmationClick}>
						{t(normalizeTKey("form:buttonTexts.save"))}
					</Button>
					<Button theme="none"
							onClick={props.onClose}>
						{t(normalizeTKey("form:buttonTexts.cancel"))}
					</Button>
				</div>
			</div>
		</Modal>
	);
}
